import { useEffect } from 'react';
import { type LiveFeedStatus } from '../../../analytics/liveFeedTelemetry';
import { SDM_EVENTS } from '../../../constants';
import { useSDMAnalytics } from '../../../contexts/AnalyticsContext';
import { useLiveFeedTelemetryCollector } from '../../../contexts/LiveFeedTelemetryContext';

/** Key for widgets whose tags match no camera, which have an error but no camera id. */
const NO_CAMERA = '';

/**
 * Report one Live Feed widget to the heartbeat collector, and track `SDM_EVENTS.LIVE_FEED_ERROR`.
 *
 * The error event fires once for each camera and error code. Every mounted copy of the widget
 * shares that record, so a drag overlay does not send the event again. It fires again only after
 * that camera plays, or when its error code changes. Without this, a failed camera in a rotation
 * would send an event on each of its turns.
 *
 * @param widgetId The widget to report, or undefined to report nothing.
 * @param status What the widget shows now.
 * @param videoElement The element that plays the visible feed. Its stalls are counted.
 */
export function useLiveFeedTelemetry(
  widgetId: string | undefined,
  status: LiveFeedStatus,
  videoElement: HTMLVideoElement | null,
): void {
  const telemetry = useLiveFeedTelemetryCollector();
  const analytics = useSDMAnalytics();

  useEffect(() => {
    if (!telemetry || !widgetId) return;
    return telemetry.register(widgetId);
  }, [telemetry, widgetId]);

  const { activeCameraId, cameraCount, streamId, state, errorCode } = status;

  useEffect(() => {
    if (!telemetry || !widgetId) return;
    telemetry.report(widgetId, { activeCameraId, cameraCount, streamId, state, errorCode });
  }, [telemetry, widgetId, activeCameraId, cameraCount, streamId, state, errorCode]);

  useEffect(() => {
    if (!telemetry || !widgetId) return;
    const cameraKey = activeCameraId ?? NO_CAMERA;

    if (state === 'playing') {
      telemetry.releaseError(widgetId, cameraKey);
      return;
    }
    if (!errorCode || !telemetry.claimError(widgetId, cameraKey, errorCode)) return;

    analytics.trackSDMEvent(SDM_EVENTS.LIVE_FEED_ERROR, {
      description: 'Live feed error',
      widgetId,
      cameraId: activeCameraId ?? undefined,
      cameraCount,
      streamId: streamId ?? undefined,
      errorCode,
    });
  }, [analytics, telemetry, widgetId, activeCameraId, cameraCount, streamId, state, errorCode]);

  useEffect(() => {
    if (!telemetry || !widgetId || !videoElement) return;

    // A preloaded slot can already play when it rotates in, so it starts as played.
    let hasPlayed =
      !videoElement.paused && videoElement.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA;
    const onPlaying = () => {
      hasPlayed = true;
    };
    // `waiting` also fires during the first load, which is not a stall.
    const onWaiting = () => {
      if (hasPlayed) telemetry.recordStall(widgetId);
    };

    videoElement.addEventListener('playing', onPlaying);
    videoElement.addEventListener('waiting', onWaiting);
    return () => {
      videoElement.removeEventListener('playing', onPlaying);
      videoElement.removeEventListener('waiting', onWaiting);
    };
  }, [telemetry, widgetId, videoElement]);
}
